import { create } from "zustand";
import faker from "faker";

export type SaleStatus = "Pagado" | "Pendiente";
export type DeliveryStatus =
  | "Enviado"
  | "Pendiente"
  | "Entregado"
  | "Cancelado"
  | "Rechazado";
export type SaleChannel = "Chat" | "Web" | "Manual";
export type ShippingMethod =
  | "Servientrega"
  | "Coordinadora"
  | "Interrapidisimo"
  | "TCC";

export interface Sale {
  id: string;
  date: string;
  status: SaleStatus;
  amount: string;
  customer: string;
  shipping: ShippingMethod;
  deliveryStatus: DeliveryStatus;
  units: number;
  channel: SaleChannel;
}

interface SalesState {
  sales: Sale[];
  filter: SaleChannel | null;
  addSale: (sale: Omit<Sale, "id">) => void;
  updateSale: (id: string, updates: Partial<Sale>) => void;
  setFilter: (filter: SaleChannel | null) => void;
  hasChanges: boolean;
  setHasChanges: (value: boolean) => void;
  saveChanges: () => void;
  discardChanges: () => void;
}

// Generate random sales data with guaranteed unique IDs
const generateRandomSales = (count: number): Sale[] => {
  const usedIds = new Set<string>();

  return Array.from({ length: count }, () => {
    let id: string;
    do {
      id = faker.datatype.uuid();
    } while (usedIds.has(id));
    usedIds.add(id);

    return {
      id,
      date: faker.date.recent(30).toLocaleDateString(),
      status: faker.random.arrayElement(["Pagado", "Pendiente"]) as SaleStatus,
      amount: "$" + faker.commerce.price(10000, 500000, 0),
      customer: faker.name.findName(),
      shipping: faker.random.arrayElement([
        "Servientrega",
        "Coordinadora",
        "Interrapidisimo",
        "TCC",
      ]) as ShippingMethod,
      deliveryStatus: faker.random.arrayElement([
        "Enviado",
        "Pendiente",
        "Entregado",
        "Cancelado",
        "Rechazado",
      ]) as DeliveryStatus,
      units: faker.datatype.number({ min: 1, max: 20 }),
      channel: faker.random.arrayElement([
        "Chat",
        "Web",
        "Manual",
      ]) as SaleChannel,
    };
  });
};

const initialSales = generateRandomSales(100);

export const useSalesStore = create<SalesState>((set) => ({
  sales: initialSales,
  filter: null,
  hasChanges: false,
  addSale: (sale) =>
    set((state) => ({
      sales: [...state.sales, { ...sale, id: faker.datatype.uuid() }],
      hasChanges: true,
    })),
  updateSale: (id, updates) =>
    set((state) => ({
      sales: state.sales.map((sale) =>
        sale.id === id ? { ...sale, ...updates } : sale
      ),
      hasChanges: true,
    })),
  setFilter: (filter) => set({ filter }),
  setHasChanges: (value) => set({ hasChanges: value }),
  saveChanges: () => set({ hasChanges: false }),
  discardChanges: () => set(() => ({ sales: initialSales, hasChanges: false })),
}));
